import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Overlay, Popover } from 'react-bootstrap';
import { Expand, ErrorIcon } from 'utils/icons';
import CommentPopup from '../CommentPopup';
import './styles.scss';

export const isTextControlTruncated = control =>
  Boolean(
    control?.clientWidth > 0 && control?.scrollWidth > control.clientWidth + 1
  );

const FormInput = React.forwardRef(
  (
    {
      error,
      hiddenColumn,
      editComment,
      type,
      label,
      placeholder,
      onClick,
      onKeyDown,
      inputClassName,
      labelClassName,
      formControlValue,
      itemType,
      rowId,
      autoFocus,
      rowData,
      helpText,
      showAutofocus,
      isComment,
      stopOnChange,
      addSpacing,
      ...rest
    },
    ref
  ) => {
    const inputRef = useRef(null);
    const detailTextTargetRef = useRef(null);
    const commentPreviewTargetRef = useRef(null);
    const detailTextHoverTimeoutRef = useRef(null);
    const commentPreviewHoverTimeoutRef = useRef(null);
    const [isClicked, setIsClicked] = useState(false);
    const [showCommentPopup, setShowCommentPopup] = useState(false);
    const [showCommentData, setShowCommentData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isControlFocused, setIsControlFocused] = useState(false);
    const [showDetailTextPopover, setShowDetailTextPopover] = useState(false);
    const [showCommentPreviewPopover, setShowCommentPreviewPopover] =
      useState(false);

    useEffect(() => {
      if (inputRef && (showAutofocus || isEditing)) {
        inputRef?.current?.focus();
      }
    }, [showAutofocus, inputRef, isEditing]);

    useEffect(() => {
      // Check if the current rowId is the one that was clicked
      if (
        itemType === 'comment' &&
        rowData?.comment !== '' &&
        rowData?.comment !== undefined
      ) {
        if (isEditing === false) {
          setIsClicked(true);
        }
      }
    }, [isEditing, itemType, rowData?.comment]);

    useEffect(
      () => () => {
        if (detailTextHoverTimeoutRef.current) {
          clearTimeout(detailTextHoverTimeoutRef.current);
        }
        if (commentPreviewHoverTimeoutRef.current) {
          clearTimeout(commentPreviewHoverTimeoutRef.current);
        }
      },
      []
    );

    const isDetailViewInputFocused = () => {
      if (typeof document === 'undefined') return false;
      const activeElement = document.activeElement;
      if (!activeElement) return false;

      const isEditableElement = ['INPUT', 'TEXTAREA', 'SELECT'].includes(
        activeElement.tagName
      );
      return (
        isEditableElement &&
        Boolean(activeElement.closest('table.detailed-view-table'))
      );
    };

    const shouldSuppressHoverPopover = () =>
      isControlFocused || isEditing || isDetailViewInputFocused();

    const clearDetailTextPopover = () => {
      if (detailTextHoverTimeoutRef.current) {
        clearTimeout(detailTextHoverTimeoutRef.current);
      }
      setShowDetailTextPopover(false);
    };

    const clearCommentPreviewPopover = () => {
      if (commentPreviewHoverTimeoutRef.current) {
        clearTimeout(commentPreviewHoverTimeoutRef.current);
      }
      setShowCommentPreviewPopover(false);
    };

    const handleStartEditing = () => {
      clearCommentPreviewPopover();
      clearDetailTextPopover();
      if (itemType === 'comment' && hiddenColumn?.comment !== true) {
        setIsEditing(true);
        setIsClicked(false);
        if (inputRef && (showAutofocus || isEditing)) {
          inputRef?.current?.focus();
        }
      }
    };

    const handleFinishEditing = () => {
      if (
        itemType === 'comment' &&
        rowData?.comment !== '' &&
        rowData?.comment !== undefined
      ) {
        setIsClicked(true);
        setIsEditing(false);
      }
    };

    const parseComment = comment => {
      const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
      const parts = comment.split(/\s+/);

      return parts?.map((part, index) => {
        if (part) {
          // const isURL = matches && matches.includes(part);
          const isURL = part && part?.match(urlRegex);
          const url = isURL
            ? part.startsWith('http')
              ? part
              : `https://${part}`
            : null;
          const isURLFlag = isURL ? isURL[0] : isURL;
          const trimmedPart =
            part.length > 48 ? `${part.slice(0, 49)}...` : part;
          if (isURLFlag?.length > 0) {
            // Handle links separately to avoid reiteration
            return (
              <>
                <a
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {trimmedPart}
                </a>
                &nbsp;
              </>
            );
          } else {
            // Render non-link parts
            return <span key={index}>{trimmedPart}&nbsp;</span>;
          }
        } else {
          return null;
        }
      });
    };

    const handleEditComment = () => {
      setShowCommentData(rowData);
      setShowCommentPopup(true);
    };

    const saveEditComment = val => {
      const obj = {
        target: {
          value: val,
        },
      };
      if (val === '' || val === undefined || val === null) {
        setIsClicked(false);
      }
      editComment(obj);
    };
    const handleClosePopup = () => {
      setShowCommentPopup(false);
    };

    const hasCommentText =
      itemType === 'comment' && Boolean(rowData?.comment?.trim?.());
    const detailTextValue = String(rest.value ?? rowData?.[itemType] ?? '');
    const hasDetailTextPopover =
      itemType === 'item' && Boolean(detailTextValue.trim());
    const isDetailTextTruncated = () =>
      isTextControlTruncated(inputRef.current);

    const handleDetailTextMouseEnter = () => {
      if (
        !hasDetailTextPopover ||
        !isDetailTextTruncated() ||
        shouldSuppressHoverPopover()
      ) {
        clearDetailTextPopover();
        return;
      }

      if (detailTextHoverTimeoutRef.current) {
        clearTimeout(detailTextHoverTimeoutRef.current);
      }

      detailTextHoverTimeoutRef.current = setTimeout(() => {
        if (shouldSuppressHoverPopover() || !isDetailTextTruncated()) return;
        setShowDetailTextPopover(true);
      }, 500);
    };

    const handleDetailTextMouseLeave = event => {
      if (event?.currentTarget?.contains(event.relatedTarget)) {
        return;
      }

      if (detailTextHoverTimeoutRef.current) {
        clearTimeout(detailTextHoverTimeoutRef.current);
      }
      setShowDetailTextPopover(false);
    };

    const handleCommentPreviewMouseEnter = () => {
      if (!hasCommentText || shouldSuppressHoverPopover()) return;

      if (commentPreviewHoverTimeoutRef.current) {
        clearTimeout(commentPreviewHoverTimeoutRef.current);
      }

      commentPreviewHoverTimeoutRef.current = setTimeout(() => {
        if (shouldSuppressHoverPopover()) return;
        setShowCommentPreviewPopover(true);
      }, 500);
    };

    const handleCommentPreviewMouseLeave = event => {
      if (event?.currentTarget?.contains(event.relatedTarget)) {
        return;
      }

      clearCommentPreviewPopover();
    };

    const renderCommentPreview = () => (
      <div
        onClick={() => handleStartEditing()}
        className={` ${inputClassName}label-cls`}
      >
        <label className={`${inputClassName}-comment-label`}>
          {parseComment(rowData.comment)}
        </label>
      </div>
    );

    const renderCommentPreviewWithPopover = () => {
      if (!hasCommentText) return renderCommentPreview();

      return (
        <>
          <div
            ref={commentPreviewTargetRef}
            className="note-hover-target"
            onMouseOver={handleCommentPreviewMouseEnter}
            onMouseOut={handleCommentPreviewMouseLeave}
          >
            {renderCommentPreview()}
          </div>
          <Overlay
            target={commentPreviewTargetRef.current}
            show={showCommentPreviewPopover && !shouldSuppressHoverPopover()}
            placement="top"
          >
            {props => (
              <Popover
                {...props}
                id={`note-preview-${rowId || rowData?.id || 'new'}`}
                className="note-hover-popover"
              >
                <Popover.Body>{rowData.comment}</Popover.Body>
              </Popover>
            )}
          </Overlay>
        </>
      );
    };

    const renderInputControl = (controlProps = {}) => (
      <Form.Control
        type={type}
        placeholder={placeholder}
        style={{ width: '100%', marginLeft: '0%' }}
        isInvalid={Boolean(error)}
        ref={refInput => {
          inputRef.current = refInput;
          if (typeof ref === 'function') {
            ref(refInput);
          }
        }}
        className={`form-input-group__input ${inputClassName}`}
        autoComplete="off"
        as={type === 'textarea' ? 'textarea' : 'input'}
        rows={3}
        {...rest}
        {...controlProps}
        onClick={e => {
          if (itemType === 'comment') {
            handleStartEditing?.();
          }
          onClick?.(e);
        }}
        onKeyDown={onKeyDown}
        onChange={e => {
          if (stopOnChange) {
            return;
          }
          if (itemType === 'comment') {
            handleStartEditing?.();
            rest.onChange(e);
          } else {
            rest.onChange(e);
          }
        }}
        autoFocus={autoFocus}
        onFocus={e => {
          setIsControlFocused(true);
          clearCommentPreviewPopover();
          clearDetailTextPopover();
          if (rest.onFocus) rest.onFocus(e);
        }}
        onBlur={e => {
          setIsControlFocused(false);
          if (itemType === 'comment') {
            handleFinishEditing?.();
          } else {
            if (rest.onBlur) rest.onBlur(e);
          }
        }}
      />
    );

    const renderInputControlWithPopover = () => {
      if (itemType !== 'item') return renderInputControl();

      return (
        <>
          <span
            ref={detailTextTargetRef}
            className="detail-text-hover-target"
            onMouseOver={handleDetailTextMouseEnter}
            onMouseOut={handleDetailTextMouseLeave}
          >
            {renderInputControl({
              onMouseOver: handleDetailTextMouseEnter,
              onMouseOut: handleDetailTextMouseLeave,
            })}
          </span>
          <Overlay
            target={detailTextTargetRef.current}
            show={
              hasDetailTextPopover &&
              isDetailTextTruncated() &&
              showDetailTextPopover &&
              !shouldSuppressHoverPopover()
            }
            placement="top"
          >
            {props => (
              <Popover
                {...props}
                id={`detail-text-preview-${rowId || rowData?.id || 'new'}`}
                className="detail-text-hover-popover"
              >
                <Popover.Body>{detailTextValue}</Popover.Body>
              </Popover>
            )}
          </Overlay>
        </>
      );
    };

    return (
      <>
        <Form.Group className="form-input-group">
          {label && (
            <Form.Label
              className={`d-flex align-item-start w-100 form-input-group__label ${labelClassName}`}
            >
              {label}
            </Form.Label>
          )}
          {!isClicked && renderInputControlWithPopover()}
          {isClicked && renderCommentPreviewWithPopover()}
          {helpText && (
            <Form.Text className="comment_txt">{helpText}</Form.Text>
          )}
          {error && (
            <Form.Control.Feedback type="invalid">
              <span className="error-icon">
                <ErrorIcon />
              </span>
              {error}
            </Form.Control.Feedback>
          )}
          {addSpacing && <div className="spacing-div">&nbsp;</div>}
          {itemType === 'comment' && hiddenColumn?.comment !== true && (
            <span className="close-icon">
              <Expand onClick={() => handleEditComment()} />
            </span>
          )}
        </Form.Group>
        {showCommentPopup === true && (
          <CommentPopup
            show={showCommentPopup}
            commentdata={showCommentData}
            handleClosePopup={handleClosePopup}
            onChange={saveEditComment}
          />
        )}
      </>
    );
  }
);

FormInput.displayName = 'FormInput';

FormInput.propTypes = {
  autoFocus: PropTypes.bool,
  type: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  helpText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  labelClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  placeholder: PropTypes.string,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  formControlValue: PropTypes.string,
  itemType: PropTypes.string,
  rowId: PropTypes.number,
  rowData: PropTypes.object,
  showAutofocus: PropTypes.bool,
  isComment: PropTypes.string,
  editComment: PropTypes.func,
  hiddenColumn: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  stopOnChange: PropTypes.bool,
  addSpacing: PropTypes.bool,
};

FormInput.defaultProps = {
  hiddenColumn: false,
  autoFocus: false,
  editComment: () => null,
  error: '',
  label: '',
  type: 'text',
  labelClassName: '',
  inputClassName: '',
  placeholder: '',
  helpText: '',
  formControlValue: '',
  itemType: '',
  onClick: () => null,
  onKeyDown: () => null,
  rowId: 0,
  showAutofocus: false,
  rowData: {},
  isComment: '',
  stopOnChange: false,
  addSpacing: true,
};

export default FormInput;
